import { ReactNode } from 'react';
import styled from 'styled-components';

import { VerticalAlignment } from 'packages/kakapo-ui-kit/types/Layout';
import { applyColorFromProps } from 'packages/kakapo-ui-kit/helpers/Theme';

interface IOuterCircleProps {
	size?: string;
	verticalAlignment?: VerticalAlignment;
	verticalMargin: number;
}

interface IChildCircleProps {
	n: number;
	delay: number;
}

interface IProps {
	size?: string;
	verticalAlignment?: VerticalAlignment;
	verticalMargin?: number;
	isLoading: boolean;
	children?: ReactNode;
	color?: string;
}

const OuterCircle = styled.div<IOuterCircleProps>`
	margin: ${(props) => props.verticalMargin}px auto;
	width: ${(props) => props.size || '40px'};
	height: ${(props) => props.size || '40px'};
	position: relative;

	${(props) =>
		props.verticalAlignment === VerticalAlignment.Middle
			? `
		position: absolute;
		left: 50%;
		top: 50%;
		transform: translate(-50%, -50%);
	`
			: ''}

	@-webkit-keyframes sk-circleBounceDelay {
		0%,
		80%,
		100% {
			-webkit-transform: scale(0);
			transform: scale(0);
		}
		40% {
			-webkit-transform: scale(1);
			transform: scale(1);
		}
	}

	@keyframes sk-circleBounceDelay {
		0%,
		80%,
		100% {
			-webkit-transform: scale(0);
			transform: scale(0);
		}
		40% {
			-webkit-transform: scale(1);
			transform: scale(1);
		}
	}
`;

const ChildCircle = styled.div<{ color?: string }>`
	width: 100%;
	height: 100%;
	position: absolute;
	left: 0;
	top: 0;

	&:before {
		content: '';
		display: block;
		margin: 0 auto;
		width: 15%;
		height: 15%;
		background-color: ${(props) => applyColorFromProps(props, 'slateBlue')};
		border-radius: 100%;
		-webkit-animation: sk-circleBounceDelay 1.2s infinite ease-in-out both;
		animation: sk-circleBounceDelay 1.2s infinite ease-in-out both;
	}
`;

const ChildCircleWithTransform = styled(ChildCircle)<IChildCircleProps>`
	-webkit-transform: ${(props) => `rotate(${(props.n - 1) * 30}deg)`};
	-ms-transform: ${(props) => `rotate(${(props.n - 1) * 30}deg)`};
	transform: ${(props) => `rotate(${(props.n - 1) * 30}deg)`};

	&:before {
		-webkit-animation-delay: ${(props) => `-${props.delay}s`};
		animation-delay: ${(props) => `-${props.delay}s`};
	}
`;

export default function Spinner({
	isLoading,
	size,
	children,
	verticalAlignment,
	verticalMargin,
	color,
}: IProps) {
	if (isLoading) {
		return (
			<OuterCircle
				size={size}
				verticalAlignment={verticalAlignment}
				verticalMargin={verticalMargin ?? 20}
			>
				<ChildCircle color={color} />
				<ChildCircleWithTransform n={2} color={color} delay={1.1} />
				<ChildCircleWithTransform n={3} color={color} delay={1} />
				<ChildCircleWithTransform n={4} color={color} delay={0.9} />
				<ChildCircleWithTransform n={5} color={color} delay={0.8} />
				<ChildCircleWithTransform n={6} color={color} delay={0.7} />
				<ChildCircleWithTransform n={7} color={color} delay={0.6} />
				<ChildCircleWithTransform n={8} color={color} delay={0.5} />
				<ChildCircleWithTransform n={9} color={color} delay={0.4} />
				<ChildCircleWithTransform n={10} color={color} delay={0.3} />
				<ChildCircleWithTransform n={11} color={color} delay={0.2} />
				<ChildCircleWithTransform n={12} color={color} delay={0.1} />
			</OuterCircle>
		);
	}
	return <>{children}</>;
}
